import { collection, getDocs, deleteDoc, query, where } from 'firebase/firestore';
import { db, shouldUseLocalStorage } from '../firebase';
import config from '../config';
import { ResultData } from '../types';
import { SurveyResult } from '../types/index';
import { isDevelopment } from '../utils/env';

// Helper to check if a result is complete
const isCompleteResult = (result: ResultData): boolean => {
  const requiredFields = [
    'beliefLevel',
    'knowledgeLevel',
    'horoscopeReading',
    'astrologicalInfluence'
  ];

  const isComplete = requiredFields.every(field => {
    const value = result[field as keyof ResultData];
    const hasValue = value && value !== '' && value !== 'Not provided';
    if (!hasValue) {
      console.log(`Missing or invalid field ${field} in result ${result.id}:`, value);
    }
    return hasValue;
  });

  console.log(`Result ${result.id} completeness check:`, {
    id: result.id,
    isComplete,
    fields: requiredFields.map(field => ({
      field,
      value: result[field as keyof ResultData],
      isValid: result[field as keyof ResultData] && result[field as keyof ResultData] !== '' && result[field as keyof ResultData] !== 'Not provided'
    }))
  });

  return isComplete;
};

// Helper to get completeness score of a result
const getCompletenessScore = (result: ResultData): number => {
  const fields = [
    'beliefLevel',
    'knowledgeLevel',
    'horoscopeReading',
    'astrologicalInfluence'
  ];

  return fields.reduce((score, field) => {
    const value = result[field as keyof ResultData];
    return score + (value && value !== '' && value !== 'Not provided' ? 1 : 0);
  }, 0);
};

// Clean up results in localStorage
const cleanupLocalStorage = (): ResultData[] => {
  console.log('Cleaning up localStorage results...');
  
  try {
    const savedData = localStorage.getItem('zodiacResultsData');
    if (!savedData) return [];

    const results: ResultData[] = JSON.parse(savedData);
    
    // Keep only complete results
    const completeResults = results.filter(isCompleteResult);
    const incompleteResults = results.filter(r => !isCompleteResult(r));
    
    // Update localStorage with only complete results
    localStorage.setItem('zodiacResultsData', JSON.stringify(completeResults));
    console.log(`Cleaned up localStorage: ${results.length} -> ${completeResults.length} results`);
    console.log(`Kept ${completeResults.length} complete results, removed ${incompleteResults.length} incomplete results`);
    
    return completeResults;
  } catch (error) {
    console.error('Error cleaning up localStorage:', error);
    return [];
  }
};

// Clean up results in Firebase
const cleanupFirebase = async (): Promise<ResultData[]> => {
  console.log('=== STARTING FIREBASE CLEANUP ===');
  console.log('Using storage:', shouldUseLocalStorage() ? 'localStorage' : 'Firestore');
  
  if (!db || shouldUseLocalStorage()) {
    console.log('Skipping Firebase cleanup - using localStorage');
    return [];
  }

  try {
    const collectionName = config.app.resultsCollection;
    console.log('Fetching results from collection:', collectionName);
    const resultsRef = collection(db, collectionName);
    const querySnapshot = await getDocs(resultsRef);
    
    console.log(`Found ${querySnapshot.size} total documents in Firestore`);
    
    const results: ResultData[] = [];
    querySnapshot.forEach(doc => {
      const data = doc.data();
      console.log(`Processing document ${doc.id}:`, {
        id: doc.id,
        fields: {
          beliefLevel: data.beliefLevel,
          knowledgeLevel: data.knowledgeLevel,
          horoscopeReading: data.horoscopeReading,
          astrologicalInfluence: data.astrologicalInfluence
        }
      });
      results.push({ id: doc.id, ...data } as ResultData);
    });
    
    // First, separate complete and incomplete results
    const completeResults = results.filter(isCompleteResult);
    const incompleteResults = results.filter(r => !isCompleteResult(r));
    
    console.log('Results breakdown:');
    console.log('- Complete results:', completeResults.length);
    console.log('- Incomplete results:', incompleteResults.length);
    
    // Delete ALL incomplete results
    let totalDeleted = 0;
    for (const result of incompleteResults) {
      console.log(`Deleting incomplete result ${result.id} (score: ${getCompletenessScore(result)})`);
      const q = query(resultsRef, where('id', '==', result.id));
      const docs = await getDocs(q);
      docs.forEach(async doc => {
        console.log(`Deleting document ${doc.id}`);
        await deleteDoc(doc.ref);
        totalDeleted++;
      });
    }
    
    console.log('\n=== CLEANUP SUMMARY ===');
    console.log(`Total documents processed: ${results.length}`);
    console.log(`Complete results kept: ${completeResults.length}`);
    console.log(`Incomplete results deleted: ${incompleteResults.length}`);
    console.log(`Total documents deleted: ${totalDeleted}`);
    console.log(`Final result count: ${completeResults.length}`);
    
    return completeResults;
  } catch (error) {
    console.error('Error cleaning up Firebase:', error);
    return [];
  }
};

// Main cleanup function
export const cleanupResults = async (): Promise<ResultData[]> => {
  console.log('=== STARTING RESULTS CLEANUP ===');
  console.log('Environment:', shouldUseLocalStorage() ? 'Development (localStorage)' : 'Production (Firestore)');
  
  if (!isDevelopment) {
    console.log('Cleanup only runs in development mode');
    return [];
  }
  
  // Clean up both storage types
  const [localResults, firebaseResults] = await Promise.all([
    Promise.resolve(cleanupLocalStorage()),
    cleanupFirebase()
  ]);
  
  // Return the cleaned results from the appropriate storage
  const finalResults = shouldUseLocalStorage() ? localResults : firebaseResults;
  console.log(`\n=== CLEANUP COMPLETE ===`);
  console.log(`Final result count: ${finalResults.length}`);
  return finalResults;
}; 